import React from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { renderCSVTable, renderArrayTable } from "./formatters/TableFormatters";
import { renderGraph } from "./formatters/GraphFormatter";
import DiaryFormatter from "./formatters/DiaryFormatter";
import DownloadButton from "../common/DownloadButton";
import PDFGenerateButton from "../common/PDFGenerateButton";
import ExcelService from "../../repositories/excel/excelService";
import { parseMessageContent } from '../../utils/chat_utils';
import { AiResponseContentType } from "../../models/chat_content";
import { useTheme } from "../../contexts/ThemeContext";
import { ChatType } from "../../models/chat_message";
import Logger from "../../utils/logger";
import { MessageExportSourceType } from "../../constants/constants";

const LoadingDots = () => (
  <div className="flex items-center gap-2 text-sm text-ui-ink-3" role="status">
    <span className="flex items-center gap-1">
      <span className="size-1.5 animate-pulse rounded-full bg-brand-500" />
      <span className="size-1.5 animate-pulse rounded-full bg-brand-500 [animation-delay:0.2s]" />
      <span className="size-1.5 animate-pulse rounded-full bg-brand-500 [animation-delay:0.4s]" />
    </span>
    Finding the answer…
  </div>
);

// Helper function to check if message content exceeds 400 characters
const shouldShowPDFButton = (content) => {
  if (!content || typeof content !== 'string') return false;
  if(content.includes(`{"dataType":`)) return true;
  return content.length > 400;
};

export const MarkdownRenderer = ({ 
  content, 
  isError = false, 
  className = "",
  style = {} 
}) => {
  const theme = useTheme();

  return (
    <div 
      className={`markdown-content ${className}`}
      style={{
        color: isError ? theme.colors.chatErrorText : theme.colors.text,
        ...style
      }}
    >
      <ReactMarkdown
        rehypePlugins={[rehypeRaw]}
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children, ...props }) => (
            <p className="mb-3 last:mb-0 text-sm leading-6" {...props}>
              {children}
            </p>
          ),
          h1: ({ children, ...props }) => (
            <h1 className="text-lg md:text-xl font-bold mt-5 mb-2.5 text-text first:mt-0 leading-snug" {...props}>
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 className="text-base md:text-lg font-bold mt-4 mb-2 text-text first:mt-0 leading-snug" {...props}>
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 className="text-sm md:text-base font-bold mt-3.5 mb-1.5 text-text first:mt-0 leading-snug" {...props}>
              {children}
            </h3>
          ),
          h4: ({ children, ...props }) => (
            <h4 className="text-xs md:text-sm font-semibold mt-3 mb-1 text-text first:mt-0 leading-snug" {...props}>
              {children}
            </h4>
          ),
          ul: ({ children, ...props }) => (
            <ul className="list-disc list-outside pl-5 space-y-1 my-3 text-sm leading-6" {...props}>
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol className="list-decimal list-outside pl-5 space-y-1 my-3 text-sm leading-6" {...props}>
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li className="leading-relaxed pl-1" {...props}>
              {children}
            </li>
          ),
          strong: ({ children, ...props }) => (
            <strong className="font-semibold text-text" {...props}>
              {children}
            </strong>
          ),
          blockquote: ({ children, ...props }) => (
            <blockquote className="border-l-[3px] border-brand-300 pl-4 py-1 my-3 text-ui-ink-2" {...props}>
              {children}
            </blockquote>
          ),
          hr: ({ ...props }) => (
            <hr className="my-5 border-table-border dark:border-table-dark-border" {...props} />
          ),
          table: ({ children, ...props }) => (
            <div className="overflow-x-auto my-4">
              <table 
                className="min-w-full border-collapse overflow-hidden rounded-lg border border-table-border dark:border-table-dark-border"
                {...props}
              >
                {children}
              </table>
            </div>
          ),
          th: ({ children, ...props }) => (
            <th
              className="px-3 py-2 text-left text-xs font-semibold bg-table-header-bg text-table-header-text dark:bg-table-dark-header-bg dark:text-table-dark-header-text border-b border-r border-table-border dark:border-table-dark-border last:border-r-0"
              {...props}
            >
              {children}
            </th>
          ),
          td: ({ children, ...props }) => (
            <td
              className="px-3 py-2 text-sm bg-table-cell-bg text-table-cell-text dark:bg-table-dark-cell-bg dark:text-table-dark-cell-text border-b border-r border-table-border dark:border-table-dark-border last:border-r-0"
              {...props}
            >
              {children}
            </td>
          ),
          tr: ({ children, ...props }) => (
            <tr
              className="hover:bg-table-hover dark:hover:bg-table-dark-hover transition-colors duration-200"
              {...props}
            >
              {children}
            </tr>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

function ChatMessage({ 
  message, 
  className = "", 
  fullWidth = false,
  showPdfExport = true,
  isPlain = false
}) {
  const theme = useTheme();

  const handleDownload = async (data, type, title) => {
    try {
      let fileName = title ? title.toLowerCase().replace(/\s+/g, '_') : `${type}_data`;
      let sheetName = title || 'Sheet1';

      ///sheetName should not exceed 31 chars. If it exceeds, change fileName to "Sheet 1"
      if (sheetName.length > 31) {
        sheetName = 'Sheet 1';
      }
      
      if (type === 'csv') {
        const rows = data.split('\n').map(row => row.split(','));
         ExcelService.downloadTableAsExcel(rows, fileName, sheetName);
      } else if (type === 'table') {
         ExcelService.downloadTableAsExcel(data, fileName, sheetName);
      }
    } catch (error) {
      Logger.error('Failed to download Excel:', error);
    }
  };

  const renderMarkdown = (text) => (
    <MarkdownRenderer content={text} isError={message?.isError} />
  );

  const renderFormattedContent = (segment) => {
    switch (segment.type) {
      case AiResponseContentType.CSV:
      case AiResponseContentType.TABLE:
        return (
          <div className="space-y-3">
            {segment.title && (
              <h3 className="text-[15px] font-semibold text-ui-ink">
                {segment.title}
              </h3>
            )}
            {segment.type === AiResponseContentType.CSV ? renderCSVTable(segment.data, theme) : renderArrayTable(segment.data, theme)}
            <div className="flex justify-end">
              <DownloadButton 
                onDownload={() => handleDownload(segment.data, segment.type, segment.title)}
                className="text-sm"
              >
                Download Excel
              </DownloadButton>
            </div>
          </div>
        );
      case AiResponseContentType.GRAPH:
        return renderGraph(segment.data, theme);
      case AiResponseContentType.LINK:
        return (
          <a 
            href={segment.data} 
            target="_blank" 
            rel="noopener noreferrer"
            className="font-medium text-ui-link underline underline-offset-2 hover:no-underline"
          >
            {segment.title || segment.data}
          </a>
        );
      case AiResponseContentType.DIARY:
        return <DiaryFormatter content={segment.data} />;
      // case "agentSwitch":
      //   return (
      //     <div className="mt-4">
      //       <ContinueButton 
      //         buttonText={segment.data.buttonText} 
      //         chatIndex={selectedChatIndex}
      //       />
      //     </div>
      //   );
      default:
        return renderMarkdown(segment.data);
    }
  };

  const renderContent = (content) => {
    const parsed = parseMessageContent(content);
    if (parsed.type === AiResponseContentType.MULTI) {
      return (
        <>
          {parsed.segments.map((segment, index) => (
            <React.Fragment key={index}>
              {segment.type === AiResponseContentType.MARKDOWN 
                ? renderMarkdown(segment.data)
                : renderFormattedContent(segment)}
            </React.Fragment>
          ))}
        </>
      );
    }

    return parsed.type === AiResponseContentType.MARKDOWN
      ? renderMarkdown(parsed.data)
      : renderFormattedContent(parsed);
  };

  if (message.role === "user") {
    return (
      <div
        className={`${fullWidth ? 'w-full' : 'max-w-[80%]'} rounded-2xl rounded-br-md px-4 py-2.5 text-sm leading-6 whitespace-pre-wrap ${className}`}
        style={{
          backgroundColor: theme.colors.chatUserBackground,
          color: theme.colors.chatUserText,
        }}
      >
        {message.content}
      </div>
    );
  }

  const errorClasses = message?.isError
    ? 'border-red-200 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10'
    : 'border-ui-line bg-ui-card';

  // `isPlain` drops the answer's own box when it already sits inside a Card (report pages)
  const boxClasses = isPlain ? '' : `rounded-xl border px-5 py-4 shadow-card ${errorClasses}`;

  return (
    <div className={`w-full min-w-0 ${boxClasses} ${className}`}>
      {message.isStreaming ? (
        <LoadingDots />
      ) : (
        <>
          <div className="space-y-4 text-ui-ink">{renderContent(message.content)}</div>

          {/* PDF export, only for longer answers (shouldShowPDFButton) */}
          {showPdfExport && shouldShowPDFButton(message.content) && (
            <div className="mt-4 flex justify-end border-t border-ui-line-soft pt-3">
              <PDFGenerateButton
                params={{
                  content: message.content,
                  messageId: message.id,
                  reportId: message.reportId,
                  reportName: `chat-message-${message.id}`,
                  type: message.type === ChatType.CHAT ? MessageExportSourceType.CHAT : MessageExportSourceType.REPORT
                }}
                size="small"
                variant="secondary"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ChatMessage; 